import { ChallengeUrls } from "@/modules/auth/common/constants";
import { UserVerificationType } from "@/modules/auth/common/enum";
import {
  useCheckVerificationCodeMutation,
  useSendVerificationCodeMutation
} from "@/modules/auth/services";
import { setChallenge, setVerificationId } from "@/modules/auth/state";
import { OTP_COUNT, OTP_PATTERN } from "@/shared/common/constants";
import { CloudflareAsset, ErrorMessages } from "@/shared/common/enums";
import { CloudFlareImage } from "@/shared/components";
import { useAppDispatch, useAppSelector, useCountDownTimer, useNotify } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { OneTimePassword } from "@ab.uitools/ui-library/components/OneTimePassword";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Props {
  isReset?: boolean;
}

export const OtpVerification = ({ isReset }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [contactInfo, setContactInfo] = useState<string>("");
  const { showErrorAlertNotification, clearAlertNotification } = useNotify();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [checkVerificationCode, { isLoading }] = useCheckVerificationCodeMutation();
  const [
    sendVerificationCode,
    { isLoading: isSendVerificationCodeLoading },
  ] = useSendVerificationCodeMutation();

  const verificationId = useAppSelector((state) => state.auth.verificationId);
  const verificationPhone = useAppSelector((state) => state.auth.verificationPhone);
  const { timer, isExpired, restartTimer } = useCountDownTimer({
    duration: 60,
  });
  const [otp, setOtp] = useState<string>("");
  const [otpError, setOtpError] = useState<string>("");

  const onSendVerificationCode = () => {
    if (!verificationId) return;
    sendVerificationCode({
      verificationId,
      verificationChannel: UserVerificationType.SMS,
    })
      .unwrap()
      .then((res) => {
        const verificationId = res?.challengeData.id;
        setContactInfo(res?.contactInfo);
        dispatch(setVerificationId(verificationId));
        restartTimer();
      });
  };

  const onSubmit = () => {
    setOtpError("");
    if (otp.length !== OTP_COUNT) {
      setOtpError(t(ErrorMessages.InvalidOtp));
    } else {
      checkVerificationCode({
        otp,
        verificationId,
        verificationChannel: UserVerificationType.SMS,
      })
        .unwrap()
        .then((res) => {
          const verificationId = res?.id || "";
          const challenge = res?.challenge as keyof typeof ChallengeUrls;
          dispatch(setVerificationId(verificationId));
          dispatch(setChallenge(challenge));
          if (!isReset && ChallengeUrls[challenge]) {
            navigate(ChallengeUrls[challenge]);
          }
        })
        .catch((err) => {
          showErrorAlertNotification(err);
        });
    }
  };

  useEffect(() => {
    clearAlertNotification();
    if (otp.length === OTP_COUNT) {
      submitButtonRef.current?.click();
    }
  }, [otp]);

  useEffect(() => {
    onSendVerificationCode();
  }, []);

  return (
    <div className="full-width otp-verification">
      {isSendVerificationCodeLoading ? (
        <div className="pt-48 pb-16">
          <Spinner size="small" isFullScreen={false}/>
        </div>
      ) : (
        <>
          <Text
            type="secondary"
            size="large"
            className="mt-24 enter-code-text"
          >
            {t("auth.otpVerification.enterCode")}
          </Text>
          <Text
            size="medium"
            className="mt-16 will-send-code-text"
            type="tertiary"
          >
            {contactInfo || t("auth.otpVerification.weWillSent", { phone: verificationPhone })}
          </Text>
          <OneTimePassword
            className="mt-16"
            placeholder="X"
            type="number"
            pattern={OTP_PATTERN}
            error={otpError}
            hasError={!!otpError}
            count={OTP_COUNT}
            onInvalid={(e) => e.preventDefault()}
            handleChange={(_, value) => setOtp(value)}
          />
          {isExpired ? (
            <div>
              <Text className="mt-16 mb-6">
                {t("auth.otpVerification.dontGetCode")}
              </Text>
              <Button
                type="text"
                className="resend-button"
                isLoading={isSendVerificationCodeLoading}
                data-testid="resend-code"
                data-ga-id="resend-code"
                data-ga-action="Resend code"
                onClick={onSendVerificationCode}
                iconProps={{
                  Component: (props: ISVGIconProps) => (
                    <CloudFlareImage {...props} path={CloudflareAsset.Refresh}/>
                  ),
                  alignment: "left",
                }}
              >
                {t("auth.otpVerification.resendAgain")}
              </Button>
            </div>
          ) : (
            <Text
              className="mt-16"
              weight="bold"
              type="tertiary"
              size="small"
            >
              {t("auth.otpVerification.resendCode", { timer })}
            </Text>
          )}
        </>
      )}
      <Button
        isLoading={isLoading}
        className="full-width mt-32"
        buttonText={t("general.confirm")}
        onClick={onSubmit}
        refHandler={submitButtonRef}
        data-testid="send-otp"
        data-ga-id="send-otp"
        data-ga-action="Send otp"
      />
    </div>
  );
};
